import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import ClickToCall from "../components/ClickToCall";
import { sourceIcon, fmtMoney } from "../lib/constants";
import { Input } from "../components/ui/input";
import { Search, PhoneCall, AlertTriangle } from "lucide-react";

export default function CallDesk() {
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");

  const { data: leads = [] } = useQuery({
    queryKey: ["leads"],
    queryFn: () => api.get("/leads").then((r) => r.data),
  });
  const { data: voiceCfg } = useQuery({
    queryKey: ["voice-config"],
    queryFn: () => api.get("/twilio/voice-config").then((r) => r.data),
  });

  const callable = leads.filter((l) => l.phone && (!q || l.name.toLowerCase().includes(q.toLowerCase())));

  return (
    <div data-testid="call-desk-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Call Desk</h1>
        <p className="text-muted-foreground mt-1">Dial leads directly from your browser and auto-log every call.</p>
      </div>

      {voiceCfg && !voiceCfg.configured && (
        <div className="flex items-center gap-2 text-sm text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 mb-5" data-testid="voice-unconfigured-banner">
          <AlertTriangle className="h-4 w-4" />
          Twilio Voice is not configured yet. Add your Twilio API key & TwiML app credentials to place browser calls.
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 rounded-2xl border border-border bg-card overflow-hidden">
          <div className="p-4 border-b border-border relative">
            <Search className="absolute left-6 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a lead..." className="pl-9" data-testid="calldesk-search" />
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {callable.map((lead) => {
              const Icon = sourceIcon(lead.source);
              return (
                <button
                  key={lead.id}
                  onClick={() => setSelected(lead)}
                  className={`w-full text-left px-4 py-3 border-b border-border flex items-center justify-between gap-2 hover:bg-secondary/40 ${selected?.id === lead.id ? "bg-primary/10" : ""}`}
                  data-testid={`calldesk-lead-${lead.id}`}
                >
                  <div className="flex items-center gap-3">
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">{lead.name}</p>
                      <p className="text-xs font-mono text-muted-foreground">{lead.phone}</p>
                    </div>
                  </div>
                </button>
              );
            })}
            {callable.length === 0 && <p className="p-6 text-sm text-muted-foreground text-center">No leads with phone numbers.</p>}
          </div>
        </div>

        <div className="lg:col-span-2">
          {selected ? (
            <div className="space-y-4">
              <div className="rounded-2xl border border-border bg-card p-6">
                <p className="text-xs font-mono uppercase tracking-widest text-primary">Now dialing</p>
                <h2 className="font-display text-2xl font-bold mt-1">{selected.name}</h2>
                <p className="text-muted-foreground text-sm mt-1">{selected.company} · {fmtMoney(selected.deal_value)}</p>
              </div>
              <ClickToCall lead={selected} />
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-border h-full min-h-[300px] flex flex-col items-center justify-center text-muted-foreground">
              <PhoneCall className="h-10 w-10 mb-3 text-primary" />
              <p>Select a lead to start calling.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
